/**
 * @file This library is for billing plan components. e.g. Calculate the amount of plan, etc.
 *  このライブラリでは、金額を表す変数名、関数名には以下のような命名規則を採用しています。
 *  これは、GraphQL の model との対応を意識したものです。
 *  - price: 税抜き金額
 *  - priceIncludingTax: 税込み金額
 *  - totalPrice: 税抜き合計金額
 *  - totalPriceIncludingTax: 税込み合計金額
 *
 *  [TODO]
 *  CheckSelectedPlanPage.tsx と CheckChangedPlanPage.tsx では、思想が逆になっていて、
 *  price: 税込み金額、 priceTaxExcluded: 税抜き金額 となっています。
 *  将来的には、このライブラリの命名規則に合わせて修正することを検討しています。
 *  ticket: https://moneyforward.atlassian.net/browse/MFXID-1935
 */
import {
  CompanyQuery,
  Coupon,
  Feature,
  Option,
  Plan,
  Service,
  ServiceStatus,
  ServiceType,
} from "@/generated/graphql_billing";
import { LOAN_SERVICE_CODE } from "@/libs/billing/constants";
import { filterActiveMikatanoServicesExceptLoan } from "@/libs/billing/service";
import { endOfMonth, startOfDay } from "date-fns";

// ==== Calculate the amount of plan, options, etc. ====
/**
 * プランの金額を返す
 * @description プランの金額を安全に返す関数。金額フィールドがない場合は0を返す。
 * @param plan
 * @returns price があればその金額を返す。なければ0を返す
 */
export const calcPlanPrice = (plan: Plan) => {
  return plan?.price ?? 0;
};

/**
 * プランの税込み金額を返す
 * @description プランの税込み金額を安全に返す関数。税込み金額フィールドがない場合は0を返す。
 */
export const calcPlanPriceIncludingTax = (plan: Plan) => {
  return plan?.priceIncludingTax ?? 0;
};

/**
 * オプションリストの税込み合計金額を返す
 * @description オプションの金額を安全に返す関数。オプションリストが空の場合は0を返す。
 * @param options
 * @returns priceIncludingTax の合計金額を返す。リストが空の場合は0を返す
 */
export const calcOptionsTotalPriceIncludingTax = (options: Option[]) => {
  return (
    options
      ?.map((option) => Math.floor(option.priceIncludingTax))
      .reduce((current, next) => current + next, 0) ?? 0
  );
};

/**
 * オプションリストの税抜き合計金額を返す
 * @description オプションの金額を安全に返す関数。オプションリストが空の場合は0を返す。
 * @param options
 * @returns price の合計金額を返す。リストが空の場合は0を返す
 */
export const calcOptionsTotalPrice = (options: Option[]) => {
  return (
    options
      ?.map((option) => Math.floor(option.price))
      .reduce((current, next) => current + next, 0) ?? 0
  );
};

/**
 * クーポンの値引き金額（絶対値）を返す
 * @description クーポンの金額を安全に返す関数。金額フィールドがない場合は0を返す。
 * @param coupon
 * @returns price があればその金額を返す。なければ0を返す
 */
export const calcCouponPrice = (coupon: Coupon) => {
  return coupon?.price ?? 0;
};

/**
 * プラン、オプション、クーポンの合計金額（税抜き）を返す
 * @param planPrice
 * @param couponPrice (> 0)
 * @param optionsPrice
 */
export const calcTotalPrice = (
  planPrice: number,
  couponPrice: number,
  optionsPrice: number
) => {
  return Math.max(0, planPrice - couponPrice) + optionsPrice;
};

/**
 * プラン、オプション、クーポンの合計金額（税込み）を返す
 * @param planPrice ()
 * @param couponPrice (> 0)
 * @param optionsPriceIncludingTax
 */
export const calcTotalPriceIncludingTax = (
  planPrice: number,
  couponPrice: number,
  optionsPriceIncludingTax: number
) => {
  return Math.max(0, planPrice - couponPrice) * 1.1 + optionsPriceIncludingTax;
};

export const calcTotalPriceWithoutTax = (plan: Plan, coupon?: Coupon) => {
  return Math.max(0, plan.price - (coupon?.price || 0));
};

export const calcTotalPriceWithTax = (plan: Plan, coupon?: Coupon) => {
  return Math.max(0, plan.price - (coupon?.price || 0)) * 1.1;
};

/**
 * 事業者の保持する有効なMikatanoサービス群の中で、あるプランの中に含まれていないサービスを返す
 * @param planSelected: Plan
 * @param company: CompanyQuery["company"]
 * @returns Service[]
 */
export const getExcludedServicesForPlan = (
  planSelected: Plan,
  company: CompanyQuery["company"]
) => {
  if (!planSelected || !company) {
    return [];
  }

  /**
   * アクティブなMikatanoサービスを取得する(融資ポータルは除く)
   */
  const activeMikatanoServices = (services) =>
    services.filter(
      (service) =>
        service.status === ServiceStatus.Active &&
        service.type === ServiceType.Mikatano &&
        service.code !== LOAN_SERVICE_CODE
    );

  /**
   * 変更後のプランに含まれていないサービスを取得する
   */
  const notInPlanSelected = (serviceBefore, selectedServices) =>
    !selectedServices.some((service) => serviceBefore.id === service.id);

  const companyServices = activeMikatanoServices(
    company.planContract.plan.services
  );
  const selectedServices = activeMikatanoServices(planSelected.services);

  return companyServices.filter((service) =>
    notInPlanSelected(service, selectedServices)
  );
};

export const diffPlanForDowngrade = (
  oldPlan: CompanyQuery["company"]["planContract"]["plan"],
  newPlan: Plan
) => {
  const oldServices = filterActiveMikatanoServicesExceptLoan(oldPlan.services);
  const newServices = filterActiveMikatanoServicesExceptLoan(newPlan.services);
  const newServiceIDs = newServices.map((svc) => svc.id);

  const removedServices = oldServices.filter(
    (oldSvc) => !newServiceIDs.includes(oldSvc.id)
  );

  const commonServices = oldServices.filter((oldSvc) =>
    newServiceIDs.includes(oldSvc.id)
  );

  const downgradedServices = [];

  for (const oldSvc of commonServices) {
    const newSvc = newServices.find((svc) => svc.id === oldSvc.id);
    if (!newSvc) {
      throw new Error("unreachable code");
    }

    const oldFeatures = oldSvc.planFeatures;
    const newFeatures = newSvc.planFeatures;
    const newFeatureIDs = newFeatures.map((feature) => feature.id);

    const removedFeatures = oldFeatures.filter(
      (oldFeature) => !newFeatureIDs.includes(oldFeature.id)
    );

    if (removedFeatures.length > 0) {
      downgradedServices.push({
        oldSvc: oldSvc,
        newSvc: newSvc,
        removedFeatures,
      });
    }
  }

  return {
    removedServices,
    downgradedServices,
  };
};

export const calcPrice = (plan?: Plan, options?: Option[], coupon?: Coupon) => {
  const planPriceExcludingTax = plan?.price ?? 0;
  const planPriceIncludingTax = plan?.priceIncludingTax ?? 0;

  const optionsTotalPriceExcludingTax =
    options
      ?.map((option) => Math.floor(option.price))
      .reduce((current, next) => current + next, 0) ?? 0;
  const optionsTotalPriceIncludingTax =
    options
      ?.map((option) => Math.floor(option.priceIncludingTax))
      .reduce((current, next) => current + next, 0) ?? 0;

  const couponPriceExcludingTax = coupon?.price ?? 0;

  const totalPriceExcludingTax =
    Math.max(0, planPriceExcludingTax - couponPriceExcludingTax) +
    optionsTotalPriceExcludingTax;
  const totalPriceIncludingTax =
    Math.max(0, planPriceExcludingTax - couponPriceExcludingTax) * 1.1 +
    optionsTotalPriceIncludingTax;

  return {
    planPriceExcludingTax,
    planPriceIncludingTax,
    optionsTotalPriceExcludingTax,
    optionsTotalPriceIncludingTax,
    couponPriceExcludingTax,
    totalPriceExcludingTax,
    totalPriceIncludingTax,
  };
};

interface UsingDater {
  usingDateFrom: any;
  usingDateTo?: any;
}

export const byActiveThisMonth =
  (now: Date) =>
  <PlanContract extends UsingDater>(pc: PlanContract) => {
    return (
      startOfDay(new Date(pc.usingDateFrom)) <= now &&
      (!pc.usingDateTo || now <= endOfMonth(new Date(pc.usingDateTo)))
    );
  };

interface ServiceTyper {
  type: ServiceType;
}

export const byServiceTypeMikatanoOrOther = <Service extends ServiceTyper>(
  svc: Service
) => svc.type === ServiceType.Mikatano || svc.type === ServiceType.Other;

interface DisplayOrderer {
  displayOrder?: number;
}

export const byDisplayOrder = <Obj extends DisplayOrderer>(a: Obj, b: Obj) => {
  return (a.displayOrder || 0) - (b.displayOrder || 0);
};

interface DefaultSortable {
  displayOrder?: number;
  createdAt?: any;
}

export const byDefaultSortOrder = <Obj extends DefaultSortable>(
  a: Obj,
  b: Obj
) => {
  if (a.displayOrder && b.displayOrder && a.displayOrder !== b.displayOrder)
    return a.displayOrder - b.displayOrder;
  if (a.displayOrder && !b.displayOrder) return -1;
  if (!a.displayOrder && b.displayOrder) return 1;
  if (a.createdAt && b.createdAt)
    return new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf();
  if (a.createdAt && !b.createdAt) return -1;
  if (!a.createdAt && b.createdAt) return 1;
  return NaN;
};

export type ActiveService = {
  id: string;
  name: string;
  code: string;
  serviceType: string;
  displayOrder: number;
  features: ActiveFeature[];
};

export type ActiveFeature = {
  id: string;
  name: string;
  code: string;
  displayOrder: number;
};

// helper function used by ContractDetailPlan in ContractDetails.tsx
export const useContractDetailPlan = (
  now: Date,
  company: CompanyQuery["company"]
) => {
  const toActiveService = (svc: Service): ActiveService => {
    return {
      id: svc.id,
      name: svc.name,
      code: svc.code,
      serviceType: svc.type,
      displayOrder: svc.displayOrder,
      features: svc.planFeatures?.map(toActiveFeature) || [],
    };
  };
  const toActiveFeature = (feature: Feature): ActiveFeature => {
    return {
      id: feature.id,
      name: feature.name,
      code: feature.code,
      displayOrder: feature.displayOrder,
    };
  };

  // cur = current  = current planContract, or service/etc that belongs to such a planContract
  //                  今契約中のplanContract/service/など
  // exp = expiring = planContract or service/etc that belongs to a planContract, that has been
  //                  canceled, but is still usable until the end of the month
  //                  解約したけれど月末まで使えるplanContract/service/など
  // diff           = things that are exp, but not in cur
  //                  curとexpの差分
  // active         = usable until the end of the month because in cur or in exp
  //                  月末まで使えるもの

  const curPlanContract = company.planContract;

  const curServices: ActiveService[] =
    curPlanContract?.plan?.services
      .filter(byServiceTypeMikatanoOrOther)
      .map(toActiveService) || [];

  const expPlanContracts =
    company.canceledPlanContracts?.filter(byActiveThisMonth(now)) || [];

  const expServices = expPlanContracts
    .flatMap((pc) => pc.plan.services.filter(byServiceTypeMikatanoOrOther))
    .map(toActiveService);

  const serviceCodeMap = new Map<string, ActiveService>();
  const featureCodeMap = new Map<string, ActiveFeature>();
  const serviceFeatureCodeMap = new Map<string, Set<string>>();

  // build service/feature superset (上位集合)
  [...curServices, ...expServices].forEach((svc) => {
    serviceCodeMap.set(svc.code, svc);

    if (!serviceFeatureCodeMap.has(svc.code)) {
      serviceFeatureCodeMap.set(svc.code, new Set());
    }
    const set = serviceFeatureCodeMap.get(svc.code);
    svc.features.forEach((f) => {
      set.add(f.code);
      featureCodeMap.set(f.code, f);
    });
  });

  // calculate diffServices - set of services that are active, but not in cur
  const curServiceCodesSet = new Set(curServices.map((svc) => svc.code));
  const expServiceCodesSet = new Set(expServices.map((svc) => svc.code));
  const diffServiceCodes = Array.from(expServiceCodesSet).filter(
    (code) => !curServiceCodesSet.has(code)
  );
  const diffServices = diffServiceCodes.map((code) => serviceCodeMap.get(code));

  // calculate features available from current services
  const curFeatureCodes = new Set<string>(
    curServices.flatMap((svc) => svc.features.map((f) => f.code))
  );

  // calculate features available from expiring services
  const expFeatureCodes = new Set<string>(
    expServices.flatMap((svc) => svc.features.map((f) => f.code))
  );

  // calculate diffFeatures - i.e. features that are only available in expiring services
  const diffFeatureCodes = Array.from(expFeatureCodes).filter(
    (code) => !curFeatureCodes.has(code)
  );

  // populate service with features
  //
  // We are creating a new active service object here as in the contract-management
  // page, we display the superset of active features for each service.
  // i.e.
  // svc A                <- although svc A comes from cur
  // - feature A          <- feature A and B can "come from" cur or exp services
  // - feature B          <-
  // svc B (利用終了予定) <- and svc B comes from exp
  // - feature C          <- feature C and D can also "come from" cur or exp services
  // - feature D

  const curServicesWithActiveFeatures = curServices.map((svc) => {
    const featureCodes = Array.from(serviceFeatureCodeMap.get(svc.code));
    const features = featureCodes
      .map((code) => featureCodeMap.get(code))
      .sort(byDisplayOrder);
    return {
      ...svc,
      features,
    };
  });

  const diffServicesWithActiveFeatures = diffServices.map((svc) => {
    const featureCodes = Array.from(serviceFeatureCodeMap.get(svc.code));
    const features = featureCodes
      .map((code) => featureCodeMap.get(code))
      .sort(byDisplayOrder);
    return {
      ...svc,
      features,
    };
  });

  curServicesWithActiveFeatures.sort(byDisplayOrder);
  diffServicesWithActiveFeatures.sort(byDisplayOrder);

  const diffFeatures = diffFeatureCodes.map((code) => featureCodeMap.get(code));

  return {
    curPlanContract,
    curServices: curServicesWithActiveFeatures,
    expPlanContracts,
    diffServices: diffServicesWithActiveFeatures,
    diffFeatures,
  };
};
